import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { StatusBadge } from '../../../global/badge/StatusBadge'
import { Button } from '../../../global/button/Button'
import IconButton from '../../../global/iconButton/IconButton'
import { useFormatNumber } from '../../../hooks/useFormatNumber'
import { CircleCloseLineIcon } from '../../../icons/CircleCloseLineIcon'
import { CircleSuccessLineIcon } from '../../../icons/CircleSuccessLineIcon'
import { SettingIcon } from '../../../icons/SettingIcon'
import { ThreeDotIsVerticalIcon } from '../../../icons/ThreeDotIsVerticalIcon'
import { NameDto } from '../../../model/NameDto'
import {
  IBRebateApprovalSortKeysDto,
  IntroducingBrokerRebateApprovalDto,
  RebateApprovalStatus,
  rebateApprovalEditableStatuses,
} from '../../../model/RebateApprovalsDto'
import { Tooltip } from '../../../ui/Popups/Tooltip/Tooltip'
import { Dropdown } from '../../../ui/v2/Dropdown/Dropdown'
import { DropdownCard } from '../../../ui/v2/Dropdown/DropdownCard'
import { DropdownList } from '../../../ui/v2/Dropdown/DropdownList'
import { DropdownListItem } from '../../../ui/v2/Dropdown/DropdownListItem'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadSort,
  TableHeader,
  TableNoResults,
  TableRow,
} from '../../../ui/v2/Table/Table'
import { TableHeaderTooltip } from '../../../ui/v2/Table/TableHeaderTooltip'
import { placeholder } from '../../../utils/placeholder'
import { IAppendableFetchResult } from '../../../utils/useFetch'
import { defaultApprovalsSort } from './hooks/useIBRebateApprovals'

import styles from './ApprovalsPageTable.module.scss'
import { Loading } from '../../../global/Loading/Loading'

interface ApprovalsPageTableProps {
  sortOptions?: NameDto<IBRebateApprovalSortKeysDto | null>[]
  sticky?: 'left'
  selectedIds?: string[]
  onSelect: (ids: string[]) => void
  onReject: (group: IntroducingBrokerRebateApprovalDto) => void
  onRecalculate: (group: IntroducingBrokerRebateApprovalDto) => void
  onApprove: (group: IntroducingBrokerRebateApprovalDto) => void
  tableQuery: IAppendableFetchResult<IntroducingBrokerRebateApprovalDto[]>
  isAnyFilterActive: boolean
}

export const ApprovalsPageTable: FC<ApprovalsPageTableProps> = (props) => {
  const { tableQuery, selectedIds = [], sortOptions, isAnyFilterActive, sticky } = props
  const { pageQuery, setPageQuery, isLoading, data: rebateApprovals } = tableQuery
  const { onReject, onRecalculate, onSelect, onApprove } = props
  const { formatNumber } = useFormatNumber()
  const { t } = useTranslation()
  const hasRowActions = rebateApprovals.some(
    ({ status }) =>
      status.id === RebateApprovalStatus.New || status.id === RebateApprovalStatus.Failed
  )
  const handleSelect = (id: string, selected: boolean) => {
    if (!selected) {
      onSelect(selectedIds?.filter((activeId) => activeId !== id))

      return
    }

    onSelect([...selectedIds, id])
  }

  return (
    <Loading isLoading={isLoading} showLoadingIcon>
    <Table sticky={sticky}>
      <TableHeader>
        <TableRow>
          {sortOptions?.map((option) =>
            option.id ? (
              <TableHeadSort
                id={option.id}
                key={option.id}
                sort={pageQuery?.sort || defaultApprovalsSort}
                sortOrder={pageQuery?.sortOrder}
                setSort={(sort, sortOrder) => {
                  setPageQuery?.({
                    ...pageQuery,
                    sort,
                    sortOrder,
                  })
                }}
              >
                {option.name}
                <TableHeaderTooltip<Partial<Record<IBRebateApprovalSortKeysDto, string>>>
                  headerIds={{
                    accumulatedVolumeFxm: t('IB.RebatesPage.Forex & Metals'),
                    accumulatedVolumeOther: t('IB.RebatesPage.Other instrument groups'),
                    accumulatedCommission: t('IB.RebatesPage.Commission accrued for TA'),
                  }}
                  targetHeaderId={option.id}
                />
              </TableHeadSort>
            ) : (
              <TableHead key={option.name}>{option.name}</TableHead>
            )
          )}
          {hasRowActions && <TableHead />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rebateApprovals.map((approval) => {
          const selected = !!selectedIds?.includes(approval?.id)
          const selectable = rebateApprovalEditableStatuses.includes(approval.status.id)

          return (
            <TableRow key={approval.id} selected={selected}>
              <TableCell
                width={190}
                onSelect={() => handleSelect(approval.id, !selected)}
                disabled={!selectable}
                checkboxTooltipText={
                  !selectable
                    ? t('IB.RebatesPage.ApprovalsPage.Cannot update finalized rebates')
                    : undefined
                }
                alert={approval.showNotification}
                selected={selected}
              >
                {approval.tradingAccount?.name}
              </TableCell>
              <TableCell width={142}>{approval.tradingAccountType.name}</TableCell>
              <TableCell width={142}>{placeholder.NA(t, approval.rebateGroup?.name)}</TableCell>
              <TableCell width={142}>
                <Tooltip.New
                  content={formatNumber(approval.accumulatedVolumeFxm)}
                  disabled={approval.accumulatedVolumeFxm < 100_000_000}
                >
                  {formatNumber(approval.accumulatedVolumeFxm, 9)}
                </Tooltip.New>
              </TableCell>
              <TableCell width={142}>
                <Tooltip.New
                  content={formatNumber(approval.accumulatedVolumeOther)}
                  disabled={approval.accumulatedVolumeOther < 100_000_000}
                >
                  {formatNumber(approval.accumulatedVolumeOther, 9)}
                </Tooltip.New>
              </TableCell>
              <TableCell width={142}>{formatNumber(approval.accumulatedCommission, 10)}</TableCell>
              <TableCell width={142}>{formatNumber(approval.rebateAmount, 10)}</TableCell>
              <TableCell>
                <StatusBadge.RebateApprovals status={approval.status.id} />
              </TableCell>
              {(approval.status.id === RebateApprovalStatus.New ||
                approval.status.id === RebateApprovalStatus.Failed) && (
                <TableCell align='right' className={styles.rowActions}>
                  <RowActions
                    status={approval.status.id}
                    onRecalculate={() => onRecalculate(approval)}
                    onReject={() => onReject(approval)}
                    onApprove={() => onApprove(approval)}
                  />
                </TableCell>
              )}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
    </Loading>
  )
}

interface RowActionsProps {
  onApprove: () => void
  onReject: () => void
  onRecalculate: () => void
  status: RebateApprovalStatus
}

export const RowActions: FC<RowActionsProps> = (props) => {
  const { onApprove, onReject, onRecalculate, status } = props

  const { t } = useTranslation()

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const allowChangeStatus =
    status === RebateApprovalStatus.New || status === RebateApprovalStatus.Failed

  if (!allowChangeStatus) {
    return null
  }

  const actions = [
    {
      label: t('IB.RebatesPage.ApprovalsPage.Approve'),
      onClick: () => {
        onApprove()
        setIsDropdownOpen(false)
      },
      icon: CircleSuccessLineIcon,
    },
    {
      label: t('IB.RebatesPage.ApprovalsPage.Reject'),
      onClick: () => {
        onReject()
        setIsDropdownOpen(false)
      },
      icon: CircleCloseLineIcon,
    },
    {
      label: t('IB.RebatesPage.ApprovalsPage.Re-calculate'),
      onClick: () => {
        onRecalculate()
        setIsDropdownOpen(false)
      },
      icon: SettingIcon,
    },
  ]

  return (
    <Dropdown
      direction='topLeft'
      isOpen={isDropdownOpen}
      setIsOpen={setIsDropdownOpen}
      content={
        <DropdownCard>
          <DropdownList>
            {actions?.map((action) => {
              return (
                <DropdownListItem
                  key={action.label}
                  onClick={action.onClick}
                  startIcon={action.icon}
                >
                  {action.label}
                </DropdownListItem>
              )
            })}
          </DropdownList>
          <div className={styles.actionsFooter}>
            <Button.New
              appearance='secondary'
              onClick={() => setIsDropdownOpen(false)}
              size='L'
              fullWidth
            >
              {t('Cancel')}
            </Button.New>
          </div>
        </DropdownCard>
      }
    >
      <IconButton.New appearance='ghost'>
        <ThreeDotIsVerticalIcon />
      </IconButton.New>
    </Dropdown>
  )
}

interface EmptyGroupsPageTableProps {
  isAnyFilterActive?: boolean
}

export const EmptyApprovalsPageTable: FC<EmptyGroupsPageTableProps> = (props) => {
  const { isAnyFilterActive } = props

  const { t } = useTranslation()

  return (
    <TableNoResults
      title={t('IB.RebatesPage.GroupsPage.Nothing to show')}
      content={isAnyFilterActive && t('IB.RebatesPage.GroupsPage.Please, adjust your filters')}
    />
  )
}
